import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@shared/lib/react-query';
import { ThemeProvider } from '@shared/components/theme-provider';
import { PortalConfigProvider } from '@shared/config';
import * as Sentry from '@sentry/react';
import '@fontsource/vollkorn/400.css';
import '@fontsource/vollkorn/700.css';
import '@fontsource/cormorant-garamond/500.css';
import '@fontsource/cormorant-garamond/600.css';
import './index.css';
import App from '@shared/App';
import { lexlabourConfig } from './config';

// Initialize Sentry before React for optimal error tracking
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
  tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '0.1'),
  integrations: [
    Sentry.browserTracingIntegration(),
  ],
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  sendDefaultPii: false,
  // Tag all events with portal identifier
  initialScope: {
    tags: { portal: 'lexlabour' },
  },
});

createRoot(document.getElementById('root')!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <PortalConfigProvider config={lexlabourConfig}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="system" storageKey={lexlabourConfig.storage.themeKey}>
          <App />
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </PortalConfigProvider>
  </StrictMode>,
);
